import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import { getAppointments, getAppointmentsByClinic, deleteAppointment } from "service/api";
import Preloader from "components/Preloader/Preloader";
import { toast } from "react-toastify";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { jwtDecode } from 'jwt-decode';

function AppointmentTable() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  // delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkRoleAndFetch();
  }, []);

  const navigate = useNavigate();

  const checkRoleAndFetch = async () => {
    try {
      const token = localStorage.getItem("token");
      let response;

      if (token) {
        const decoded = jwtDecode(token);
        const clinicID = decoded?.clinicID;

        if (clinicID) {
          setIsAdmin(false); // clinic user
          response = await getAppointmentsByClinic(clinicID);
        } else {
          setIsAdmin(true); // admin
          response = await getAppointments();
        }
      } else {
        setIsAdmin(true); // default → admin
        response = await getAppointments();
      }

      if (response?.data?.data) {
        setAppointments(response.data.data.reverse());
      }
    } catch (error) {
      console.log("Error while fetching appointments:", error);
      toast.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  };

  const openViewModel = (id) => {
    navigate(`/AppointmentDetails/${id}`);
  };

  // 🔹 Delete Modal Handlers
  const openDeleteModal = (id) => {
    setSelectedDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedDeleteId(null);
    setIsDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    try {
      await deleteAppointment(selectedDeleteId);
      toast.success("Appointment deleted successfully!");
      checkRoleAndFetch(); // refresh list
    } catch (error) {
      console.error("Error while deleting appointment:", error);
      toast.error("Failed to delete appointment");
    } finally {
      closeDeleteModal();
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Appointment No.",
        accessor: "appointment_no",
      },
      {
        Header: "Patient Name",
        accessor: "patient_name",
      },
      {
        Header: "Patient MPI No.",
        accessor: "patient_mpi",
        Cell: ({ row }) => {
          const mpi = row.original.patient_mpi;
          const idno = row.original.patient_idno;

          return mpi ? (
            <span>{mpi}</span>
          ) : (
            <div>
              <span className="badge bg-danger">Unregistered</span>
              <br />
              <span>{idno}</span>
            </div>
          );
        },
      },
      {
        Header: "Appointment Date",
        accessor: "appointment_date",
        Cell: ({ value }) => {
          if (!value) return "-";
          const date = new Date(value);
          return (
            <span>
              {String(date.getDate()).padStart(2, "0")}-
              {String(date.getMonth() + 1).padStart(2, "0")}-
              {date.getFullYear()}
            </span>
          );
        },
      },
      {
        Header: "Practice Schedule",
        accessor: "appointment_time",
      },
      {
        Header: "Status",
        accessor: "appointment_status",
        Cell: ({ value }) => (
          <span
            className={`badge ${value === "Approved" ? "bg-success" : "bg-warning"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Action",
        Cell: ({ row }) => {
          const id = row.original._id;
          const patient = row.original;

          return (
            <div className="d-flex">
              <button
                className="btn btn-warning mx-2"
                title="View"
                onClick={() => openViewModel(id)}
              >
                <i className="mdi mdi-eye font-size-15"></i>
              </button>

              <button
                className="btn btn-danger mx-2"
                title="Delete"
                onClick={() => openDeleteModal(id)}
              >
                <i className="mdi mdi-trash-can font-size-15"></i>
              </button>

              {/* ✅ Show only if patient is unregistered */}
              {!patient.patient_mpi && (
                <button
                  className="btn btn-success mx-2"
                  title="Register Patient"
                  onClick={() => {
                    const nameParts = (patient.patient_name || "").trim().split(" ");
                    const firstname = nameParts[0] || "";
                    const lastname = nameParts.slice(1).join(" ") || "";
                    navigate("/Addpatient", {
                      state: {
                        firstname,
                        lastname,
                        idnumber: patient.patient_idno,
                        gender: patient.gender,
                        dob: patient.patient_dob,
                        email: patient.email,
                        mobile: patient.mobileno,
                        clinicname: patient.apnt_clinicid,
                        title: patient.patient_title,
                      },
                    });
                  }
                  }
                >
                  <i className="mdi mdi-account-plus font-size-15"></i>
                </button>
              )}
            </div>
          );
        },
      }
    ];

    // ✅ Agar Admin hai to Practice column add karo
    if (isAdmin) {
      baseColumns.unshift({
        Header: "Practice",
        accessor: "clinic_name",
      });
    }

    // Sr. No. always first
    baseColumns.unshift({
      Header: "Sr. No.",
      accessor: (_row, i) => i + 1,
      id: "srno",
      Cell: ({ value }) => <span style={{ fontWeight: "600", color: "#888" }}>{value}</span>,
    });

    return baseColumns;
  }, [isAdmin]);

  //meta title
  document.title = "Appointment | List";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="page-title">Appointment List</h3>
          <Link to="/Appointment" className="btn btn-primary">
            ADD NEW Appointment
          </Link>
        </div>
        {/* <Breadcrumbs breadcrumbItem="Appointment List" /> */}
        <div>
          {/* <TableContainer
            columns={columns}
            data={appointments}
            isGlobalFilter={true}
            isAddOptions={false}
            customPageSize={10}
            className="custom-header-css"
          /> */}
          <TableContainer
            columns={columns}
            data={appointments}
            isGlobalFilter={true}
            isAddOptions={false}
            customPageSize={10}
            className="custom-header-css"
            isCustomPageSize={true}
            isPagination={true}
            defaultSorted={[
              {
                id: "appointment_no",
                desc: true,   // 👈 important (descending)
              },
            ]}
          />
          {loading && <Preloader />}
        </div>
      </div>

      {/* 🔹 Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal} centered>
        <ModalHeader toggle={closeDeleteModal}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this appointment?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
          <Button color="secondary" onClick={closeDeleteModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default AppointmentTable;
